import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { Button, Dropdown } from '@robinpowered/ui-kit';
import {
  FilterButton,
  FilterButtonContent,
  overlayPopupStyles,
} from '../styles';
import { useFilterOptionKeyboardNav } from '../hooks/useFilterOptionKeyboardNav';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faListUl,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import { GlobalSearchEntityType } from 'generated/graphql';

interface CategoryFilterProps {
  selectedCategory: GlobalSearchEntityType | null;
  onChange: (category: GlobalSearchEntityType | null) => void;
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  selectedCategory,
  onChange,
}) => {
  const { t } = useTranslation('globalNavigation', {
    keyPrefix: 'searchOverlay',
  });
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const options: { value: GlobalSearchEntityType; label: string }[] = useMemo(
    () => [
      { value: GlobalSearchEntityType.User, label: t('categoryFilter.people') },
      {
        value: GlobalSearchEntityType.Space,
        label: t('categoryFilter.spaces'),
      },
      { value: GlobalSearchEntityType.Desk, label: t('categoryFilter.desks') },
    ],
    [t]
  );

  // Close the dropdown and return focus to the trigger so Tab continues to the
  // next control instead of falling back to the start of the modal.
  const closeDropdown = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const selectCategory = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option) {
        return;
      }
      onChange(option.value === selectedCategory ? null : option.value);
      closeDropdown();
    },
    [options, selectedCategory, onChange, closeDropdown]
  );

  const { activeIndex } = useFilterOptionKeyboardNav({
    open,
    optionCount: options.length,
    onSelect: selectCategory,
    onClose: closeDropdown,
  });

  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    const options =
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    options?.[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectedLabel = options.find(
    (option) => option.value === selectedCategory
  )?.label;

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      styles={overlayPopupStyles}
      popupRender={() => (
        <FilterPanel
          ref={listRef}
          role="listbox"
          aria-label={t('categoryFilter.groupLabel')}
        >
          {options.map((option, index) => {
            const isSelected = option.value === selectedCategory;
            const isActive = index === activeIndex;
            return (
              <CategoryOption
                key={option.value}
                type="text"
                role="option"
                aria-selected={isSelected}
                data-active={isActive}
                onClick={() => selectCategory(index)}
              >
                {option.label}
              </CategoryOption>
            );
          })}
        </FilterPanel>
      )}
    >
      <FilterButton ref={triggerRef} type="text">
        <FilterButtonContent>
          <FontAwesomeIcon icon={faListUl} />
          <span>
            {selectedLabel
              ? t('categoryFilter.triggerSelected', { category: selectedLabel })
              : t('category')}
          </span>
          <FontAwesomeIcon icon={faChevronDown} />
        </FilterButtonContent>
      </FilterButton>
    </Dropdown>
  );
};

const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
  padding: 8px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
`;

const CategoryOption = styled(Button)`
  justify-content: flex-start;
  width: 100%;
  min-height: 36px;
  text-align: left;

  &[aria-selected='true'] {
    background: #f5f5f5;
    font-weight: 600;
  }

  &&[data-active='true'] {
    background: #e6f4ff;
  }
`;
